package com.islamicmedia.shared.domain.model

/**
 * When to ask the OS to show its built-in store review card: after EVERY [EVERY]th completed
 * analysis (5, 10, 15, ...), counted per device, mobile only, never in Kids Mode, never on web
 * (those gates live in MediaCheckerViewModel; this object is only the counting rule).
 *
 * Why only the OS card: Apple guideline 5.6.1 forbids custom review prompts and Google forbids
 * asking the user a question before its card, so the app asks the OS and nothing else. The OS
 * decides whether the card actually appears (Apple shows it about 3 times a year at most; Google
 * has its own quota), so a "prompt" here is a request, not a guarantee.
 *
 * State: SettingsStorage.analysisCount is the number of completed analyses; the stored
 * "last prompt at" count is the analysis count when we last asked. A prompt is due once the count
 * has grown by [EVERY] since then — comparing against the last ask (not `count % EVERY == 0`)
 * means a missed milestone (the user never left the result screen, signed out, the app was
 * killed) is picked up on the next completed analysis instead of being lost.
 */
internal object ReviewPromptRule {
    /** Ask after every this-many completed analyses. */
    const val EVERY = 5

    /** True when [count] completed analyses warrants a request, given the count at the last one. */
    fun shouldPrompt(count: Int, lastPromptAt: Int): Boolean =
        count >= EVERY && count - lastPromptAt >= EVERY

    /**
     * Starting "last prompt at" for an install that has no stored value yet: the highest multiple
     * of [EVERY] not above [count]. Someone who already has 7 analyses is not prompted on their
     * next one (seed 5, so the next prompt is at 10), and a brand-new install seeds 0 (first
     * prompt at 5).
     */
    fun seedLastPromptAt(count: Int): Int = count - count % EVERY
}
